import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { BeforeAfterSlider } from "@/components/ui/before-after-slider";
import beforeScreenshot from "@/assets/before-screenshot.png";
import afterScreenshot from "@/assets/after-screenshot.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const BeforeAfterSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section ref={sectionRef} className="px-4 sm:px-6 py-12 sm:py-16 bg-gradient-subtle" aria-labelledby="before-after-heading">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          className="text-center mb-12" 
          variants={staggerContainer} 
          initial="hidden" 
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 
            id="before-after-heading" 
            className="text-3xl font-bold text-foreground mb-4" 
            variants={fadeInUp}
          >
            See the transformation
          </motion.h2>
          <motion.p 
            className="text-lg text-muted-foreground/90 mb-8" 
            variants={fadeInUp}
          >
            Experience how YakStak transforms your research workflow
          </motion.p>
        </motion.div>

        <motion.div 
          className="space-y-12"
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Main comparison */}
          <motion.div className="space-y-6" variants={fadeInUp}>
            <div className="text-center">
              <h3 className="text-xl font-semibold text-foreground mb-2">From chaos to clarity</h3>
              <p className="text-muted-foreground">Drag the slider to compare your research workflow before and after YakStak</p>
            </div>
            <BeforeAfterSlider
              beforeImage={beforeScreenshot}
              afterImage={afterScreenshot}
              beforeAlt="Research workflow before YakStak - scattered notes and confusion"
              afterAlt="Research workflow after YakStak - organized insights and clear decisions"
              className="w-full h-[400px] mx-auto max-w-4xl"
            />
          </motion.div>

          {/* Benefits highlight */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="space-y-2">
              <div className="text-2xl font-bold text-primary">10x</div>
              <div className="text-sm text-muted-foreground">Faster analysis</div>
            </motion.div>
            <motion.div variants={fadeInUp} className="space-y-2">
              <div className="text-2xl font-bold text-primary">90%</div>
              <div className="text-sm text-muted-foreground">Less manual work</div>
            </motion.div>
            <motion.div variants={fadeInUp} className="space-y-2">
              <div className="text-2xl font-bold text-primary">100%</div>
              <div className="text-sm text-muted-foreground">Better decisions</div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};